// The app's own concept of a team member. Field names and the role vocabulary
// are OURS, not ACE's — the mapper translates ACE's wire shape into this. Every
// component depends on this model and never on the ACE DTO.

export enum TeamRole {
  Admin = 'Admin',
  Ops = 'Ops',
  Gp = 'Gp',
  Lp = 'Lp',
  Supporter = 'Supporter',
  // Any role ACE introduces that we don't yet recognise.
  Unknown = 'Unknown',
}

export enum TeamPermission {
  Administrator = 'Administrator',
  Manager = 'Manager',
  User = 'User',
  Unknown = 'Unknown',
}

export interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: TeamRole[];
  permission?: TeamPermission;
  phone?: string;
  location?: string;
  position?: string;
  dateOfBirth?: string;
  avatarUrl: string | null;
  // Set when this member is the logged-in user (the team list excludes the
  // caller, so we prepend them from /users/me).
  isCurrentUser: boolean;
}

// Human-facing label for a role. The single place that decides how each role
// reads in the UI — independent of whatever string ACE happens to send.
const ROLE_LABELS: Record<TeamRole, string> = {
  [TeamRole.Admin]: 'Admin',
  [TeamRole.Ops]: 'Ops',
  [TeamRole.Gp]: 'GP',
  [TeamRole.Lp]: 'LP',
  [TeamRole.Supporter]: 'Supporter',
  [TeamRole.Unknown]: 'Other',
};

const PERMISSION_LABELS: Record<TeamPermission, string> = {
  [TeamPermission.Administrator]: 'Administrator',
  [TeamPermission.Manager]: 'Manager',
  [TeamPermission.User]: 'User',
  [TeamPermission.Unknown]: 'Other',
};

// --- Pure helpers: the single home for logic the components used to duplicate.

export function fullName(member: TeamMember): string {
  return `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
}

export function initials(member: TeamMember): string {
  const first = member.firstName?.charAt(0) ?? '';
  const last = member.lastName?.charAt(0) ?? '';
  return `${first}${last}`.toUpperCase() || '?';
}

export function roleLabel(role: TeamRole): string {
  return ROLE_LABELS[role] ?? ROLE_LABELS[TeamRole.Unknown];
}

export function hasRole(member: TeamMember, role: TeamRole): boolean {
  return member.roles.includes(role);
}

export function permissionForRoles(
  roles: readonly (string | null | undefined)[]
): TeamPermission {
  const normalized = new Set(
    roles
      .filter((role): role is string => typeof role === 'string')
      .map(role => role.trim().toUpperCase())
  );
  if (normalized.has('ADMIN') || normalized.has('GP')) {
    return TeamPermission.Administrator;
  }
  if (normalized.has('OPS')) return TeamPermission.Manager;
  if (normalized.has('SUPPORTER')) return TeamPermission.User;
  return TeamPermission.Unknown;
}

export function permissionLabel(permission?: TeamPermission): string {
  return PERMISSION_LABELS[permission ?? TeamPermission.Unknown];
}
